<!-- src/views/HealthandSafety/AccidentWitnessStatementFormView.vue -->
<template>
  <div class="min-h-screen bg-[#F0F0F0]">
    <FormHeader backTo="HealthSafetyFormsList" backLabel="Back to H&S List" />

    <div class="p-4 sm:p-6 md:p-8">
      <div class="max-w-7xl mx-auto text-left">
        
        <!-- Premium Header Card -->
        <div class="bg-[#0A1630] rounded-t-[40px] p-8 border-b-4 border-[#20FCF1] shadow-2xl">
          <div class="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div>
              <h1 class="text-3xl font-black text-white uppercase tracking-tight">
                {{ isPublic && clientName ? `${clientName}: ` : '' }}Witness Statement
              </h1>
              <p class="text-[#20FCF1] font-black uppercase text-[10px] tracking-[0.2em] mt-2">
                Official Incident Testimony • Sentinel Safe Premium
              </p>
            </div>
            <div class="bg-white/5 px-4 py-2 rounded-xl border border-white/10 flex items-center gap-3">
              <div class="w-2 h-2 bg-[#20FCF1] rounded-full animate-pulse"></div>
              <span class="text-white text-[10px] font-black uppercase tracking-widest">Evidence Mode</span>
            </div>
          </div>
        </div>

        <div class="bg-white p-6 sm:p-10 rounded-b-[40px] shadow-xl border-x border-b border-slate-200">
          
          <div v-if="isLoadingText" class="flex flex-col items-center py-20 bg-slate-50 rounded-[32px] border-2 border-dashed border-slate-200">
            <div class="animate-spin h-10 w-10 border-4 border-[#20FCF1] border-t-transparent rounded-full mb-4"></div>
            <p class="text-[10px] font-black uppercase tracking-[0.2em] text-slate-400">Loading Testimony Standards...</p>
          </div>

          <form v-else @submit.prevent="handleSubmit" class="space-y-16">
            
            <!-- Step 1: Witness Details -->
            <section>
              <div class="flex items-center gap-4 mb-8">
                <div class="bg-[#20FCF1] text-[#0A1630] w-12 h-12 rounded-2xl flex items-center justify-center shadow-lg">
                  <IdentificationIcon class="h-6 w-6" />
                </div>
                <h2 class="text-2xl font-black text-[#0A1630] uppercase tracking-tight">1. Witness Identity</h2>
              </div>

              <div class="grid grid-cols-1 md:grid-cols-2 gap-8 bg-slate-50 p-8 rounded-[32px] border border-slate-100 shadow-inner">
                <div>
                  <label class="form-label">Full Name <span class="text-red-500">*</span></label>
                  <input type="text" v-model="formData.witnessName" required class="form-input" placeholder="Full Legal Name" />
                </div>
                <div>
                  <label class="form-label">Email Address <span class="text-red-500">*</span></label>
                  <input type="email" v-model="formData.witnessEmail" required class="form-input" placeholder="email@example.com" />
                </div>
                <div>
                  <label class="form-label">Contact Phone <span class="text-red-500">*</span></label>
                  <input type="tel" v-model="formData.witnessPhone" required class="form-input" placeholder="Primary phone number" />
                </div>
                <div>
                  <label class="form-label">Sentinel Number <span class="text-red-500">*</span></label>
                  <input type="text" v-model="formData.witnessSentinel" required class="form-input" placeholder="PTS No." />
                </div>
                <div>
                  <label class="form-label">Company Position <span class="text-red-500">*</span></label>
                  <select v-model="formData.witnessPosition" required class="form-input font-bold">
                    <option disabled value="">Select Title</option>
                    <option v-for="option in jobTitleOptions" :key="option.value" :value="option.value">{{ option.text }}</option>
                  </select>
                </div>
                <div v-if="formData.witnessPosition === 'other'">
                  <label class="form-label">Specify Position <span class="text-red-500">*</span></label>
                  <input type="text" v-model="formData.witnessPositionOther" required class="form-input" />
                </div>
                <div>
                  <label class="form-label">Immediate Manager <span class="text-red-500">*</span></label>
                  <input type="text" v-model="formData.witnessManager" required class="form-input" placeholder="Enter manager's name" />
                </div>
              </div>
            </section>

            <!-- Step 2: Incident Context (Relational Auto-Fill Implemented) -->
            <section>
              <div class="flex items-center gap-4 mb-8">
                <div class="bg-[#20FCF1] text-[#0A1630] w-12 h-12 rounded-2xl flex items-center justify-center shadow-lg">
                  <CalendarDaysIcon class="h-6 w-6" />
                </div>
                <h2 class="text-2xl font-black text-[#0A1630] uppercase tracking-tight">2. Incident Context</h2>
              </div>

              <div class="grid grid-cols-1 md:grid-cols-2 gap-8 bg-slate-50 p-8 rounded-[32px] border border-slate-100 shadow-inner">
                <div>
                   <label class="form-label">Accident Reference <span class="text-red-500">*</span></label>
                   <select v-model="formData.accidentReference" required class="form-input font-bold appearance-none bg-white">
                      <option disabled value="">Select Related Accident...</option>
                      <option v-for="acc in accidentForms" :key="acc.id" :value="acc.reference">
                         {{ acc.reference }} - {{ acc.userName }}
                      </option>
                      <option value="Not Listed / Unknown">Not Listed / Unknown</option>
                   </select>
                </div>
                <div>
                  <label for="accidentDateTime" class="form-label">Date & Time of Accident <span class="text-red-500">*</span></label>
                  <input type="datetime-local" id="accidentDateTime" v-model="formattedAccidentDateTime" required class="form-input" />
                </div>
                <div class="md:col-span-2">
                  <label class="form-label">Location <span class="text-red-500">*</span></label>
                  <input type="text" v-model="formData.location" required class="form-input" placeholder="Specify exact site location" />
                </div>
              </div>
            </section>

            <!-- Step 3: Witness Testimony -->
            <section>
              <div class="flex items-center gap-4 mb-8">
                <div class="bg-[#20FCF1] text-[#0A1630] w-12 h-12 rounded-2xl flex items-center justify-center shadow-lg">
                  <ChatBubbleBottomCenterTextIcon class="h-6 w-6" />
                </div>
                <h2 class="text-2xl font-black text-[#0A1630] uppercase tracking-tight">3. Witness Testimony</h2>
              </div>

              <div class="bg-slate-50 p-8 rounded-[32px] border border-slate-100 shadow-inner">
                 <p class="text-[10px] font-black uppercase tracking-widest text-slate-400 mb-4">Please provide a detailed chronological account of the event</p>
                 <textarea v-model="formData.statement" rows="10" required class="form-input" placeholder="Type your statement here..."></textarea>
              </div>
            </section>
            
            <!-- Step 4: Declaration -->
             <section v-if="statementText">
                <div class="flex items-center gap-4 mb-8">
                  <div class="bg-[#20FCF1] text-[#0A1630] w-12 h-12 rounded-2xl flex items-center justify-center shadow-lg">
                    <DocumentTextIcon class="h-6 w-6" />
                  </div>
                  <h2 class="text-2xl font-black text-[#0A1630] uppercase tracking-tight">4. Declaration</h2>
                </div>
                
                <div class="p-8 bg-slate-50 border border-slate-200 rounded-[32px] shadow-inner mb-6">
                  <div class="ql-editor !p-0 text-slate-700" v-html="statementText"></div>
                </div>

                <div class="bg-slate-900 p-10 rounded-[40px] text-white shadow-xl relative overflow-hidden">
                    <div class="absolute top-0 right-0 p-10 opacity-5">
                      <CheckBadgeIcon class="h-32 w-32" />
                    </div>
                    <div class="relative z-10 flex flex-col md:flex-row items-center justify-between gap-8">
                        <div class="max-w-xl">
                          <h2 class="text-2xl font-black text-[#20FCF1] uppercase tracking-tight mb-2">Legal Confirmation</h2>
                          <p class="text-slate-400 font-medium leading-relaxed">I believe that the facts stated in this witness statement are true and accurate.</p>
                        </div>
                        <div class="bg-white/5 p-6 rounded-[32px] border border-white/10">
                            <div class="flex items-center">
                                <input id="statementConfirmation" type="checkbox" v-model="formData.statementConfirmation" class="h-10 w-10 text-[#20FCF1] border-white/20 rounded-xl bg-transparent transition-all">
                                <label for="statementConfirmation" class="ml-4 block text-xs font-black uppercase tracking-widest text-white leading-tight">Certify<br>Testimony <span class="text-red-500">*</span></label>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

             <!-- Step 5: Secure Attachments -->
            <section>
               <div class="flex items-center gap-4 mb-8">
                <div class="bg-[#20FCF1] text-[#0A1630] w-12 h-12 rounded-2xl flex items-center justify-center shadow-lg">
                  <PaperClipIcon class="h-6 w-6" />
                </div>
                <h2 class="text-2xl font-black text-[#0A1630] uppercase tracking-tight">5. Secure Attachments</h2>
              </div>
               <div class="grid grid-cols-1 md:grid-cols-3 gap-6">
                 <div class="p-6 bg-[#EBFDF8] rounded-[32px] border-2 border-dashed border-[#20FCF1]/40">
                   <label class="form-label text-[#0A1630] mb-4">Photos & Sketches</label>
                   <ImageUploader id="witness-upload" label="Add Media" @update:files="updateImageFiles" />
                 </div>
                 <div class="p-6 bg-slate-50 rounded-[32px] border-2 border-dashed border-slate-200">
                   <label class="form-label text-slate-400 mb-4">Documentation</label>
                   <DocumentUploader id="doc-upload" label="Add Docs" @update:files="updateDocumentFiles" />
                 </div>
                 <div class="p-6 bg-slate-50 rounded-[32px] border-2 border-dashed border-slate-200">
                   <label class="form-label text-slate-400 mb-4">Training Certificates</label>
                   <DocumentUploader id="cert-upload" label="Add Certs" @update:files="updateCertificationFiles" />
                 </div>
               </div>
            </section>
            
            <!-- Step 6: Signatures -->
             <section>
              <div class="flex items-center gap-4 mb-8">
                <div class="bg-[#20FCF1] text-[#0A1630] w-12 h-12 rounded-2xl flex items-center justify-center shadow-lg">
                  <PencilSquareIcon class="h-6 w-6" />
                </div>
                <h2 class="text-2xl font-black text-[#0A1630] uppercase tracking-tight">6. Signatures</h2>
              </div>

              <div class="grid grid-cols-1 md:grid-cols-2 gap-8">
                <!-- Part A: Reported By -->
                <div class="bg-slate-50 p-8 rounded-[40px] border border-slate-100 flex flex-col items-center shadow-inner">
                  <div class="w-full space-y-6">
                    <h3 class="text-sm font-black uppercase tracking-widest text-[#0A1630] mb-2 border-b border-[#20FCF1]/30 pb-2">Part A: Reported By</h3>
                    <div>
                      <label class="form-label text-slate-400">Full Name <span class="text-red-500">*</span></label>
                      <input type="text" v-model="formData.reportedByName" required class="form-input bg-white" />
                    </div>
                    <div>
                      <label class="form-label text-slate-400">Company <span class="text-red-500">*</span></label>
                      <input type="text" v-model="formData.reportedByCompany" required class="form-input bg-white" placeholder="Client Name" />
                    </div>
                    <div>
                      <label class="form-label text-slate-400">Report Date <span class="text-red-500">*</span></label>
                      <input type="date" v-model="formattedReportedDate" required class="form-input bg-white" />
                    </div>
                    
                    <div class="bg-white p-4 rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
                      <label class="block text-[9px] font-black uppercase text-slate-400 mb-3 text-center">Digital Seal Required</label>
                      <Vue3Signature ref="reportedBySignaturePad" :sigOption="signatureOptions" :h="'180px'" :w="'100%'" class="border border-slate-100 rounded-xl"></Vue3Signature>
                      <div class="mt-4 flex gap-2">
                          <button type="button" @click="saveReportedBySignature" class="flex-1 py-2 bg-[#0A1630] text-white rounded-lg font-black uppercase text-[10px] tracking-widest hover:bg-slate-800 transition-all">Confirm Seal</button>
                          <button type="button" @click="clearReportedBySignature" class="px-4 py-2 bg-slate-100 text-slate-500 rounded-lg font-black uppercase text-[10px] tracking-widest hover:bg-slate-200">Clear</button>
                      </div>
                    </div>

                    <div v-if="formData.reportedBySigDataUrl" class="p-4 bg-[#EBFDF8] rounded-2xl flex items-center justify-center border border-[#20FCF1]/20 animate-fadeIn mt-4">
                        <img :src="formData.reportedBySigDataUrl" alt="Seal Preview" class="max-h-16 object-contain" />
                    </div>
                  </div>
                </div>

                <!-- Part B: Witnessed By -->
                <div class="bg-slate-50 p-8 rounded-[40px] border border-slate-100 flex flex-col items-center shadow-inner">
                  <div class="w-full space-y-6">
                    <h3 class="text-sm font-black uppercase tracking-widest text-[#0A1630] mb-2 border-b border-[#20FCF1]/30 pb-2">Part B: Witnessed By</h3>
                    <div>
                      <label class="form-label text-slate-400">Full Name <span class="text-red-500">*</span></label>
                      <input type="text" v-model="formData.witnessedByName" required class="form-input bg-white" />
                    </div>
                    <div>
                      <label class="form-label text-slate-400">Company <span class="text-red-500">*</span></label>
                      <input type="text" v-model="formData.witnessedByCompany" required class="form-input bg-white" />
                    </div>
                    <div>
                      <label class="form-label text-slate-400">Witness Date <span class="text-red-500">*</span></label>
                      <input type="date" v-model="formattedWitnessedDate" required class="form-input bg-white" />
                    </div>

                    <div class="bg-white p-4 rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
                      <label class="block text-[9px] font-black uppercase text-slate-400 mb-3 text-center">Digital Seal Required</label>
                      <Vue3Signature ref="witnessedBySignaturePad" :sigOption="signatureOptions" :h="'180px'" :w="'100%'" class="border border-slate-100 rounded-xl"></Vue3Signature>
                      <div class="mt-4 flex gap-2">
                          <button type="button" @click="saveWitnessedBySignature" class="flex-1 py-2 bg-[#0A1630] text-white rounded-lg font-black uppercase text-[10px] tracking-widest hover:bg-slate-800 transition-all">Confirm Seal</button>
                          <button type="button" @click="clearWitnessedBySignature" class="px-4 py-2 bg-slate-100 text-slate-500 rounded-lg font-black uppercase text-[10px] tracking-widest hover:bg-slate-200">Clear</button>
                      </div>
                    </div>

                    <div v-if="formData.witnessedBySigDataUrl" class="p-4 bg-[#EBFDF8] rounded-2xl flex items-center justify-center border border-[#20FCF1]/20 animate-fadeIn mt-4">
                        <img :src="formData.witnessedBySigDataUrl" alt="Seal Preview" class="max-h-16 object-contain" />
                    </div>
                  </div>
                </div>
              </div>
            </section>

            <!-- Actions -->
            <div class="pt-10 flex flex-col md:flex-row gap-4 no-print">
              <button type="submit" :disabled="isSubmitting || !isFormValid" class="btn-premium flex-1">
                <span v-if="isSubmitting" class="flex items-center justify-center gap-3">
                    <svg class="animate-spin h-6 w-6" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>
                    Filing Official Testimony...
                </span>
                <span v-else>Submit Final Witness Statement</span>
              </button>
              <button type="button" @click="handleCancel" class="px-10 py-6 bg-slate-200 text-slate-700 rounded-[32px] text-lg font-black uppercase tracking-widest hover:bg-slate-300 transition-all">
                Cancel
              </button>
            </div>

            <p v-if="successMessage" class="mt-8 p-6 bg-[#EBFDF8] border-2 border-[#20FCF1]/20 rounded-[40px] text-center shadow-lg animate-bounce font-black text-[#0A1630] uppercase tracking-widest">{{ successMessage }}</p>
            <p v-if="submissionError" class="mt-4 text-sm text-red-600 font-bold text-center animate-pulse">{{ submissionError }}</p>
          </form>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted, computed, watch } from 'vue';
import { useRouter } from 'vue-router';
import { db, auth, storage, firebaseApp } from '@/firebaseConfig'; 
import { doc, getDoc, getDocs, collection, query, where } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { ref as storageRef, uploadBytes, getDownloadURL } from 'firebase/storage';
import ImageUploader from '@/components/ImageUploader.vue';
import DocumentUploader from '@/components/DocumentUploader.vue';
import Vue3Signature from 'vue3-signature';
import FormHeader from '@/components/FormHeader.vue';

import { 
  IdentificationIcon, CalendarDaysIcon, 
  ChatBubbleBottomCenterTextIcon, DocumentTextIcon, PaperClipIcon, 
  PencilSquareIcon, CheckBadgeIcon
} from '@heroicons/vue/24/outline';

const router = useRouter();
const functions = getFunctions(firebaseApp, 'europe-west2');

const props = defineProps({
    clientId: { type: String, default: '' }
});

const isSubmitting = ref(false);
const submissionError = ref('');
const successMessage = ref('');
const statementText = ref('');
const isLoadingText = ref(true);
const isPublic = ref(false);
const activeClientId = ref('');
const clientName = ref('');

const imageFiles = ref([]);
const documentFiles = ref([]);
const certificationFiles = ref([]);
const accidentForms = ref([]);

const reportedBySignaturePad = ref(null);
const witnessedBySignaturePad = ref(null);
const signatureOptions = { penColor: "#0A1630", backgroundColor: "rgba(255,255,255,0)" };

const jobTitleOptions = ref([]);

const initialFormData = {
  isConfidential: false,
  userName: '', userEmail: '',
  witnessName: '', witnessEmail: '', witnessPhone: '', witnessSentinel: '', witnessPosition: '', witnessPositionOther: '', witnessManager: '',
  accidentReference: '', accidentDateTime: null, location: '',
  statement: '', statementConfirmation: false,
  reportedByName: '', reportedByCompany: '', reportedByDate: null, reportedBySigDataUrl: '',
  witnessedByName: '', witnessedByCompany: '', witnessedByDate: null, witnessedBySigDataUrl: '',
};

const formData = reactive({ ...initialFormData });

const formattedAccidentDateTime = computed({
  get: () => {
    if (!formData.accidentDateTime) return '';
    const d = new Date(formData.accidentDateTime);
    const z = (n) => (n < 10 ? '0' : '') + n;
    return `${d.getFullYear()}-${z(d.getMonth() + 1)}-${z(d.getDate())}T${z(d.getHours())}:${z(d.getMinutes())}`;
  },
  set: (val) => { formData.accidentDateTime = val ? new Date(val) : null; }
});

const formattedReportedDate = computed({
  get: () => formData.reportedByDate ? new Date(formData.reportedByDate).toISOString().split('T')[0] : '',
  set: (val) => { formData.reportedByDate = val ? new Date(val) : null; }
});

const formattedWitnessedDate = computed({
  get: () => formData.witnessedByDate ? new Date(formData.witnessedByDate).toISOString().split('T')[0] : '',
  set: (val) => { formData.witnessedByDate = val ? new Date(val) : null; }
});

const isFormValid = computed(() => {
    if (isLoadingText.value) return false;
    const statementConfirmed = statementText.value ? formData.statementConfirmation : true;
    const requiredFields = ['witnessName', 'witnessEmail', 'witnessPhone', 'witnessSentinel', 'witnessPosition', 'witnessManager', 'location', 'statement', 'reportedByName', 'reportedByCompany', 'witnessedByName', 'witnessedByCompany', 'accidentReference'];
    const textFieldsValid = requiredFields.every(field => formData[field] && String(formData[field]).trim() !== '');
    const datesValid = !!formData.accidentDateTime && !!formData.reportedByDate && !!formData.witnessedByDate;
    const signaturesValid = !!formData.reportedBySigDataUrl && !!formData.witnessedBySigDataUrl;
    return statementConfirmed && textFieldsValid && datesValid && signaturesValid && !isSubmitting.value;
});

const updateImageFiles = (files) => imageFiles.value = files;
const updateDocumentFiles = (files) => documentFiles.value = files;
const updateCertificationFiles = (files) => certificationFiles.value = files;

const saveReportedBySignature = () => { if (reportedBySignaturePad.value) formData.reportedBySigDataUrl = reportedBySignaturePad.value.save('image/png'); };
const clearReportedBySignature = () => { if (reportedBySignaturePad.value) reportedBySignaturePad.value.clear(); formData.reportedBySigDataUrl = ''; };
const saveWitnessedBySignature = () => { if (witnessedBySignaturePad.value) formData.witnessedBySigDataUrl = witnessedBySignaturePad.value.save('image/png'); };
const clearWitnessedBySignature = () => { if (witnessedBySignaturePad.value) witnessedBySignaturePad.value.clear(); formData.witnessedBySigDataUrl = ''; };

const dataURLtoBlob = (dataurl) => {
    const arr = dataurl.split(',');
    const mime = arr[0].match(/:(.*?);/)[1];
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    while(n--){ u8arr[n] = bstr.charCodeAt(n); }
    return new Blob([u8arr], {type:mime});
};

const fetchAccidentForms = async (targetClientId) => {
    try {
        const q = query(
            collection(db, 'healthsafetyFormSubmissions'), 
            where('clientId', '==', targetClientId),
            where('formIdentifier', '==', 'accidentForm')
        );
        const snap = await getDocs(q);
        accidentForms.value = snap.docs.map(doc => {
            const d = doc.data();
            return {
                id: doc.id,
                reference: d.humanReadableId || 'SS-ACC-TBC',
                userName: d.userName || 'Anonymous',
                // 🛡️ RELATIONAL DATA MAPPING
                accidentDateTime: d.accidentDateTime || null,
                location: d.location || ''
            };
        }).sort((a,b) => b.reference.localeCompare(a.reference));
    } catch (err) {
        console.error("Accident Registry Error:", err);
    }
};

/** 🛡️ RELATIONAL AUTO-FILL HANDSHAKE */
watch(() => formData.accidentReference, (newRef) => {
    if (!newRef || newRef === 'Not Listed / Unknown') return;
    const selected = accidentForms.value.find(a => a.reference === newRef);
    if (selected) {
        formData.location = selected.location;
        // Firestore timestamps or ISO strings
        if (selected.accidentDateTime) {
            formData.accidentDateTime = selected.accidentDateTime.toDate ? selected.accidentDateTime.toDate() : new Date(selected.accidentDateTime);
        }
    }
});

const fetchFormOptions = async (targetClientId) => {
  try {
    const docSnap = await getDoc(doc(db, 'formOptions', 'witnessJobTitles'));
    if (docSnap.exists()) {
      const data = docSnap.data();
      let options = (targetClientId && data[targetClientId]) ? data[targetClientId] : (data.global || []);
      jobTitleOptions.value = [...options].sort((a, b) => (a.text || "").localeCompare(b.text || ""));
    }
  } catch (error) { console.error("Options error:", error); }
};

const loadPrerequisites = async () => { 
  isLoadingText.value = true;
  submissionError.value = '';

  try {
      let targetId = props.clientId;
      const user = auth.currentUser;

      if (!targetId && user) {
          const idTokenResult = await user.getIdTokenResult();
          targetId = idTokenResult.claims.clientId;
          if (!targetId) {
            const pSnap = await getDoc(doc(db, 'userProfiles', user.uid));
            targetId = pSnap.data()?.clientId;
          }
      }

      if (!targetId) targetId = 'default';

      const result = await httpsCallable(functions, 'getPublicHealthSafetyContext')({ clientId: targetId });

      if (result.data) {
          clientName.value = result.data.clientName;
          statementText.value = result.data.formTexts?.accidentWitnessStatement || '';
          activeClientId.value = (targetId === 'default' ? '' : targetId);
          isPublic.value = !!props.clientId;
          
          /** 🛡️ SEQUENTIAL DATA LOAD */
          await Promise.all([
             fetchFormOptions(activeClientId.value),
             fetchAccidentForms(activeClientId.value)
          ]);
      }

      if (user && !props.clientId) {
        const pSnap = await getDoc(doc(db, 'userProfiles', user.uid));
        if (pSnap.exists()) {
          const p = pSnap.data();
          
          formData.userName = `${p.firstName || ''} ${p.lastName || ''}`.trim();
          formData.userEmail = p.email || '';
          
          formData.witnessName = formData.userName;
          formData.witnessEmail = formData.userEmail;
          formData.witnessPhone = p.mobilePhoneNumber || '';
          formData.witnessSentinel = p.sentinelNumber || '';
          formData.witnessPosition = p.jobTitle || ''; 
          
          const profileSig = p.signatureDataUrl || p.signatureUrl;
          if (profileSig) formData.reportedBySigDataUrl = profileSig;

          formData.reportedByName = formData.userName;
          
          // 🛡️ AUTO-POPULATE COMPANY NAME (Now using backend context resolution)
          formData.reportedByCompany = clientName.value || 'TECS';
          formData.reportedByDate = new Date();
        }
      }
  } catch (err) { submissionError.value = "Failed to load requirements."; }
  isLoadingText.value = false;
};

onMounted(() => loadPrerequisites());

const uploadAtomicFiles = async (files, folder) => {
  const timestamp = Date.now();
  const uploadPromises = files.map(async (file) => {
    const cleanName = file.name.replace(/[^a-z0-9.]/gi, '_');
    const filePath = `clients/${activeClientId.value}/healthsafetyFormSubmissions/atomic_${timestamp}/${folder}/${cleanName}`;
    const fileRef = storageRef(storage, filePath);
    await uploadBytes(fileRef, file);
    return getDownloadURL(fileRef);
  });
  return await Promise.all(uploadPromises);
};

const uploadSignature = async (dataUrl, path) => {
    if (!dataUrl || !dataUrl.startsWith('data:image')) return dataUrl;
    const blob = dataURLtoBlob(dataUrl);
    const fileRef = storageRef(storage, path);
    await uploadBytes(fileRef, blob);
    return getDownloadURL(fileRef);
};

const handleSubmit = async () => {
  submissionError.value = '';
  if (!isFormValid.value) return;
  isSubmitting.value = true;

  try {
    const sigPath = `clients/${activeClientId.value}/healthsafetyFormSubmissions/sigs_${Date.now()}`;
    
    const [finalImageUrls, finalDocUrls, finalCertUrls, rUrl, wUrl] = await Promise.all([
        uploadAtomicFiles(imageFiles.value, 'witnessImages'),
        uploadAtomicFiles(documentFiles.value, 'witnessDocs'),
        uploadAtomicFiles(certificationFiles.value, 'witnessCerts'),
        uploadSignature(formData.reportedBySigDataUrl, `${sigPath}/reportedBy.png`),
        uploadSignature(formData.witnessedBySigDataUrl, `${sigPath}/witnessedBy.png`)
    ]);

    const result = await httpsCallable(functions, 'submitGenericForm')({
      formIdentifier: 'accidentWitnessStatementForm', 
      targetCollection: 'healthsafetyFormSubmissions',
      submissionData: { 
        ...formData, 
        userName: formData.userName, 
        userEmail: formData.userEmail,
        reportedBySigDataUrl: rUrl,
        witnessedBySigDataUrl: wUrl,
        imageUrls: finalImageUrls,
        documentUrls: finalDocUrls,
        certificationUrls: finalCertUrls,
        accidentDateTime: formData.accidentDateTime.toISOString(),
        reportedByDate: formData.reportedByDate.toISOString(),
        witnessedByDate: formData.witnessedByDate.toISOString(),
        isPublicSubmission: isPublic.value, 
        clientUid: activeClientId.value, 
        submittedAt: new Date().toISOString() 
      }
    });

    if (!result.data.success) throw new Error('Submission failed.');
    
    successMessage.value = `Witness Statement Registry Sync Complete! Reference: ${result.data.referenceNumber}`;
    setTimeout(() => {
        if (isPublic.value) window.location.reload();
        else router.push({ name: 'completedForm', params: { collectionName: 'healthsafetyFormSubmissions', submissionId: result.data.submissionId } });
    }, 2000);

  } catch (error) {
    submissionError.value = error.message;
    isSubmitting.value = false;
  }
};

const handleCancel = () => {
  if (window.confirm('Cancel and lose changes?')) {
    if (isPublic.value) router.push({ name: 'PublicHealthSafetyFormsList', params: { clientId: props.clientId } });
    else router.push({ name: 'HealthSafetyFormsList' });
  }
};
</script>

<style scoped>
.form-input { @apply block w-full px-4 py-3 bg-white border border-slate-200 rounded-xl text-slate-700 shadow-sm transition-all focus:ring-2 focus:ring-[#20FCF1] focus:border-[#20FCF1] sm:text-sm font-bold; }
.form-label { @apply block text-[11px] font-black text-[#0A1630] uppercase tracking-[0.2em] mb-2; }
.btn-premium { @apply w-full bg-[#20FCF1] text-[#0A1630] py-6 rounded-[32px] text-xl font-black uppercase tracking-[0.2em] shadow-2xl hover:scale-[1.02] active:scale-95 transition-all disabled:opacity-50 disabled:grayscale flex justify-center items-center; }
.animate-fadeIn { animation: fadeIn 0.4s ease-out forwards; }
@keyframes fadeIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
:deep(.ql-editor) { font-family: inherit; font-size: 0.95rem; line-height: 1.8; color: #334155; }
:deep(.ql-editor p) { margin-bottom: 1.25rem; }
:deep(.ql-editor h2) { @apply text-xl font-black uppercase tracking-tight text-slate-900 mt-8 mb-4; }
</style>